import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ArrowLeft, ChevronRight, Eye, Lock, LogOut, Menu, Search } from 'lucide-react';
import { Kbd, Toasts } from '@dxl/ui';
import { ansichtImTab, ansichtImTabBeenden, api } from '../lib/api';
import { darfPlattform, kontextWechseln, meAbfrage, sitzungLeeren } from '../lib/sitzung';
import { initialen, navigationFuer, navZiel } from '../lib/navigation';
import { istMac, useTaste } from '../lib/tastatur';
import { Befehlspalette } from './Befehlspalette';
import { DxlZustimmung } from './DxlZustimmung';
import { GuthabenFehltDialog } from './KiGuthaben';

export function Shell() {
  const { data: me } = useQuery(meAbfrage);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const [menueOffen, setMenueOffen] = useState(false);
  const [palette, setPalette] = useState(false);
  const paletteAuf = useCallback(() => setPalette(true), []);
  const paletteZu = useCallback(() => setPalette(false), []);
  useTaste('k', paletteAuf, { mitBefehl: true });

  // Beim Abmelden ist die Sitzung kurz leer, bevor die Anmeldeseite übernimmt
  if (!me) return null;
  const gruppen = navigationFuer(me);
  const b = me.mandant ? me.mandant.branding : me.plattformBranding ?? undefined;
  // Dunkles Erscheinungsbild: Logo für dunkle Flächen, sonst das helle Logo, zuletzt das DXL-Logo
  const logo = b?.erscheinungsbild === 'dunkel' ? (b.logoDunkel ?? b.logoHell ?? '/dxl-logo-on-dark.svg') : (b?.logoHell ?? '/dxl-logo.svg');
  // Ohne Whitelabel liefert der Server immer dxlHinweis = true
  const dxlSichtbar = !me.mandant || me.mandant.branding.dxlHinweis;

  async function abmelden() {
    if (!me) return;
    await api('/api/auth/abmelden', { methode: 'POST' }).catch(() => undefined);
    sitzungLeeren(qc);
    await navigate({ to: me.mandant ? '/m/$kuerzel/anmelden' : '/anmelden', params: { kuerzel: me.mandant?.kuerzel.toLowerCase() ?? '' } });
  }

  async function ansichtBeenden() {
    await api('/api/auth/ansicht-beenden', { methode: 'POST' });
    if (ansichtImTab()) {
      // eigener Tab: schließen; geht das nicht (Tab nicht per Skript geöffnet), hier zur Plattform
      ansichtImTabBeenden();
      window.close();
    }
    await kontextWechseln(qc);
    await navigate({ to: '/marktplaetze', search: {} });
  }

  return (
    <div className={`shell ${menueOffen ? 'shell--menue' : ''}`}>
      <aside className="seitenleiste" aria-label="Hauptnavigation">
        <div className="seitenleiste__kopf">
          <img src={logo} alt={me.mandant?.name ?? 'DXL'} className="seitenleiste__logo" />
          <span className="seitenleiste__bereich">{me.mandant ? me.mandant.name : 'Commerce Plattform'}</span>
        </div>

        <button type="button" className="seitenleiste__suche" onClick={paletteAuf}>
          <Search aria-hidden />
          <span>Suchen</span>
          <span className="seitenleiste__kuerzel">
            <Kbd>{istMac ? '⌘' : 'Strg'}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        <nav className="seitenleiste__nav" onClick={() => setMenueOffen(false)}>
          {gruppen.map((g, i) => (
            <div key={g.titel ?? i} className="seitenleiste__gruppe">
              {g.titel && g.uebersicht ? (
                <Link
                  {...navZiel(g.uebersicht)}
                  className={`seitenleiste__gruppentitel seitenleiste__gruppentitel--link ${pfad === `/${g.uebersicht.id}` ? 'aktiv' : ''}`}
                  aria-current={pfad === `/${g.uebersicht.id}` ? 'page' : undefined}
                  title={`Übersicht ${g.titel}`}
                >
                  {g.titel}
                  <ChevronRight aria-hidden />
                </Link>
              ) : g.titel && <p className="seitenleiste__gruppentitel">{g.titel}</p>}
              {g.punkte.map((p) => {
                const aktiv = pfad === `/${p.id}`;
                const S = p.symbol;
                return (
                  <Link key={p.id} {...navZiel(p)} className={`navlink ${aktiv ? 'aktiv' : ''}`} aria-current={aktiv ? 'page' : undefined}>
                    <S className="navlink__symbol" aria-hidden />
                    <span>{p.titel}</span>
                    {p.gesperrt && <Lock className="navlink__schloss" aria-label="Nicht im Paket enthalten" />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="seitenleiste__fuss">
          {dxlSichtbar && me.mandant && <p className="seitenleiste__dxl">Bereitgestellt von DXL</p>}
          <div className="konto">
            <span className="konto__avatar" aria-hidden>{initialen(me.nutzer.name)}</span>
            <Link to="/konto" className="konto__daten" title="Mein Konto">
              <span className="konto__name">{me.nutzer.name}</span>
              <span className="konto__email">{me.nutzer.email}</span>
            </Link>
            <button type="button" className="konto__abmelden" onClick={abmelden} title="Abmelden" aria-label="Abmelden">
              <LogOut aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      <div className="arbeitsflaeche">
        <header className="mobilkopf">
          <button type="button" className="mobilkopf__menue" aria-expanded={menueOffen} aria-label="Menü" onClick={() => setMenueOffen((o) => !o)}>
            <Menu aria-hidden />
          </button>
          <img src={logo} alt="" height={20} />
          <button type="button" className="mobilkopf__menue" aria-label="Suchen" onClick={paletteAuf}>
            <Search aria-hidden />
          </button>
        </header>
        <main className={`inhalt ${me.ansicht ? 'inhalt--ansicht' : ''}`}>
          {me.ansicht && me.mandant && (
            <div className="ansichtsleiste" role="status">
              <Eye aria-hidden />
              <span>
                <strong>DXL-Ansicht:</strong>{' '}
                {darfPlattform(me, 'marktplaetze_bearbeiten')
                  ? <>Sie arbeiten im Konto von {me.mandant.name}. Änderungen werden unter Ihrem Namen protokolliert.</>
                  : <>Sie sehen das Konto von {me.mandant.name} an. Mit Ihrem Rechte-Level sind keine Änderungen möglich.</>}
              </span>
              <button type="button" onClick={ansichtBeenden}>
                <ArrowLeft aria-hidden /> Zur Plattform
              </button>
            </div>
          )}
          {me.mandant && !me.ansicht && <DxlZustimmung />}
          <Outlet />
        </main>
      </div>

      <Befehlspalette me={me} offen={palette} onSchliessen={paletteZu} />
      {me.mandant && <GuthabenFehltDialog />}
      <Toasts />
    </div>
  );
}
